/// <reference types="react" />

import * as React from "react";
import * as ReactDOM from "react-dom";

import { DefaultButton, PrimaryButton } from "OfficeFabric/Button";
import { CopyButton } from "VSSPreview/Flux/Components/CopyButton";
import { Checkbox } from "VSSUI/Checkbox";
import { Dialog } from "OfficeFabric/Dialog";
import { DialogType, IDialogContentProps } from "OfficeFabric/components/Dialog/DialogContent.types";
import { DialogFooter } from "OfficeFabric/components/Dialog/DialogFooter";
import { IModalProps } from "OfficeFabric/Modal";
import { Link } from "VSSUI/Link";
import { LicenseAndFeatureFlagUtils } from "TestManagement/Scripts/Utils/TFS.TestManagement.LicenseAndFeatureFlagUtils";
import { ObservableValue } from "VSS/Core/Observable";
import { getCookie, setCookie } from "Presentation/Scripts/TFS/TFS.Core.Utils";

import * as Resources from "TestManagement/Scripts/Resources/TFS.Resources.TestManagement";
import TCMTelemetry = require("TestManagement/Scripts/TFS.TestManagement.Telemetry");
import TCMLite = require("TestManagement/Scripts/TFS.TestManagement.Lite");

import * as ComponentBase from "VSS/Flux/Component";

import "VSS/LoaderPlugins/Css!fabric";
import "VSS/LoaderPlugins/Css!TestManagement/Scripts/Scenarios/RunWithDTRDialog/Components/RunWithDTRDialog";
import { format } from "VSS/Utils/String";

let TelemetryService = TCMTelemetry.TelemetryService;

export interface IRunWithDTRDialogProps extends ComponentBase.Props {
    dtrCallBack: () => void;
}

export interface IRunWithDTRDialogState extends ComponentBase.State {
    showDialog: boolean;
}

export function renderDialog(element: HTMLElement, RunWithDTRDialogProps: IRunWithDTRDialogProps): void {
    ReactDOM.render(<RunWithDTRDialog {...RunWithDTRDialogProps} />, element);
}

export class RunWithDTRDialog extends ComponentBase.Component<IRunWithDTRDialogProps, IRunWithDTRDialogState> {
    private _testRunnerDownloadLink: string;
    private _learnMoreLink: string;
    private _checkboxChecked: ObservableValue<boolean>;

    constructor(props: IRunWithDTRDialogProps) {
        super(props);
        this._checkboxChecked = new ObservableValue<boolean>(false);
        this.state = { showDialog: true } as IRunWithDTRDialogState;
    
        if (LicenseAndFeatureFlagUtils.isShowDTRUpdateMsgLaunchDialogEnabled()) {
            // hideDTRLaunchDialogCookieState is a popup state of the launch dialog.
            // "true" then this means pop up is hidden.
            // "" means cookie is not created/deleted. this state will display the launch popup.
            const hideDTRLaunchDialogCookieState = getCookie(TCMLite.Constants.HideDTRLaunchDialogCookieName);
            if (hideDTRLaunchDialogCookieState === "true" || hideDTRLaunchDialogCookieState === "") {
                //sets the cookie value to show the launch DTR dialog
                setCookie(TCMLite.Constants.HideDTRLaunchDialogCookieName, "falseAlways");
                const showVersionUpdateMessage = getCookie(TCMLite.Constants.ShowDTRVersionUpdateMessageCookieName);
                if (showVersionUpdateMessage === "") {
                    let date = new Date();
                    date.setTime(date.getTime() + 7 * 24 * 60 * 60 * 1000);
                    //setting the cookie expire in 7 days
                    const expires = "; expires=" + date.toUTCString();
                    //create a cookie to maintain the state of new version for update message. 
                    setCookie(TCMLite.Constants.ShowDTRVersionUpdateMessageCookieName, "true", "/ ;" + expires);
                }
            }
        }
    }

    public render(): JSX.Element {
        this._testRunnerDownloadLink = "https://aka.ms/ATPTestRunnerDownload";
        this._learnMoreLink = "https://aka.ms/ATPTestRunnerLearnMore";

        const launchButtonClassName = "run-with-dtr-dialog-launch-btn";
        let dialogcontentProps: IDialogContentProps = {
            title: Resources.RunTestWithDTRText,
            className: "run-with-dtr-dialog-content",
            closeButtonAriaLabel: Resources.CloseText,
            type: DialogType.close,
            showCloseButton: true
        };
        let modalProps: IModalProps = {
            className: "run-with-dtr-dialog bowtie-fabric",
            containerClassName: "run-with-dtr-dialog-container",
            isBlocking: true
        };

        return (
            <Dialog
                dialogContentProps={dialogcontentProps}
                modalProps={modalProps}
                hidden={!this.state.showDialog}
                onDismiss={this._onCancelClick}
                firstFocusableSelector={launchButtonClassName}
            >
                <div className="run-with-dtr-dialog-info-section">
                    {Resources.TestRunnerDialogInstallationInfoText}
                    <Link href={this._learnMoreLink} target={"_blank"} className="run-with-dtr-dialog-info-link">
                        {Resources.LearnMoreText}
                    </Link>
                </div>
                <div className="run-with-dtr-dialog-acq-section">
                    <PrimaryButton className="run-with-dtr-dialog-download-dialog" text={Resources.Download} onClick={this._onDownloadDTRClick} />
                    <div className="run-with-dtr-dialog-acq-section-separator">|</div>
                    <CopyButton
                        cssClass="run-with-dtr-dialog-acq-link-container"
                        copyTitle={Resources.TestRunnerLinkCopytoClipboardTooltip}
                        copiedTitle={Resources.TestRunnerLinkCopiedtoClipboardTooltip}
                        copyText={this._testRunnerDownloadLink}
                        copyAsHtml={false}
                    />
                </div>
                {LicenseAndFeatureFlagUtils.isShowDTRUpdateMsgLaunchDialogEnabled() &&
                getCookie(TCMLite.Constants.ShowDTRVersionUpdateMessageCookieName) === "true" ? (
                    <div className="run-with-dtr-dialog-info-newatrversion">
                        <p>
                            <mark>
                                <b>{Resources.NewATRVersionPrefix}</b>
                                {format(Resources.NewATRVersionText, Resources.LatestATRVersion)}
                            </mark>
                        </p>
                    </div>
                ) : null}
                <div className="run-with-dtr-launch-info-container">{Resources.TestRunnerLaunchInstructionsText}</div>
                <Checkbox
                    className="run-with-dtr-dialog-optout-checkbox"
                    checked={this._checkboxChecked}
                    onChange={this._onCheckBoxStateChanged.bind(this)}
                    label={Resources.TestRunnerDialogCheckboxText}
                />
                <DialogFooter className="run-with-dtr-dialog-footer">
                    <PrimaryButton onClick={this._onOkClick} text={Resources.LaunchText} className={launchButtonClassName} />
                    <DefaultButton onClick={this._onCancelClick} text={Resources.CancelText} />
                </DialogFooter>
            </Dialog>
        );
    }

    private _onCheckBoxStateChanged = (ev: React.FormEvent<HTMLElement | HTMLInputElement>, checked: boolean = false) => {
        this._checkboxChecked.value = checked;
    };

    private _onDownloadDTRClick = () => {
        TelemetryService.publishEvents(TelemetryService.featureRunWithTestRunnerATP_DownloadClicked, {});
        window.open(this._testRunnerDownloadLink, "_blank");
    };

    /**
     * Launch DTR here.
     */
    private _onOkClick = () => {
        this._setCookieState();
        TelemetryService.publishEvents(TelemetryService.featureRunWithTestRunnerATP_LaunchClicked, { userAgent: window.navigator.userAgent });
        this.props.dtrCallBack();
        this._closeDialog();
    };

    private _onCancelClick = () => {
        this._closeDialog();
    };

    private _closeDialog(): void {
        this.setState({ showDialog: false });
    }

    /**
     * cookie used to check for not showing this dialog again.
     */
    private _setCookieState(): void {
        if (this._checkboxChecked.value) {
            if (LicenseAndFeatureFlagUtils.isShowDTRUpdateMsgLaunchDialogEnabled()) {
                // "trueAlways" the version update message is already shown and new hide the launch popup as the checkbox is set to true. 
                setCookie(TCMLite.Constants.HideDTRLaunchDialogCookieName, "trueAlways");
            } else {
                setCookie(TCMLite.Constants.HideDTRLaunchDialogCookieName, "true");
            }
        }
    }
}